import React from "react";
import { PageHero, Reveal, Overline, SectionHeading, CTAButton } from "@/components/primitives";
import { CtaStrip } from "@/components/sections";
import SiteImage from "@/components/SiteImage";
import { EVENT } from "@/data/site";

export default function About() {
  return (
    <>
      <PageHero
        overline="About"
        title="India's ethanol decade, and the room building what's next."
        subtitle="Bharat Ethanol Forum exists because the question changed — from can India scale? to what does India build next?"
        image="/assets/site/about-ethanol-refinery.jpg"
        imageFallback="https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      />

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline className="mb-4">The context</Overline>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">From 1.5% to 20% in a decade.</h2>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-navy/70">
                <p>India has run one of the fastest fuel transitions in the world. Ethanol blending in petrol rose from 1.5% in 2014 to 20% in 2025 — hitting the E20 target five years early — while production capacity grew from under 2 billion litres to nearly 20 billion. In May 2026, the country notified fuel standards for blends up to E30.</p>
                <p>The question is no longer whether India can scale. It's what India builds next: E30 and flex-fuel vehicles, grain and 2G ethanol, compressed biogas, sustainable aviation fuel and integrated bio-refineries.</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <SiteImage name="about-sugarcane-feedstock" ratio="4/5" alt="Sugarcane feedstock fuelling India's ethanol economy" fallback="https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-border bg-white p-8 shadow-sm">
                <p className="font-display text-6xl font-semibold tracking-tight text-emerald2">20%</p>
                <p className="mt-2 text-navy/70">ethanol blended in India's petrol by 2025 — five years ahead of the 2030 target, up from 1.5% in 2014.</p>
                <p className="mt-4 text-xs text-navy/40">Source: Ministry of Petroleum & Natural Gas, 2025.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Overline className="mb-4">The platform</Overline>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">One table, the whole value chain.</h2>
              <p className="mt-6 text-lg leading-relaxed text-navy/70">
                Bharat Ethanol Forum is a two-day leadership forum built for exactly that question. It brings producers, oil marketing companies, automakers, technology providers, investors and policymakers to one table to shape the post-E20 roadmap. Running alongside it is the {EVENT.expo}, a focused technology marketplace. Together they cover the full arc of India's ethanol economy — feedstock to fuel, fuel to chemicals, and India to global markets.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <SiteImage name="about-indian-policy-leadership" ratio="16/9" alt="Senior leaders in a high-level policy roundtable" fallback="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy grain py-20 md:py-28">
        <div className="pointer-events-none absolute -right-20 top-0 h-80 w-80 rounded-full bg-emerald2/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline light className="mb-4">The organiser</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">30+ years of putting industries in one room.</h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Tefla's builds international conferences, exhibitions and policy platforms across agriculture, energy, sustainability and global trade. The focus is simple: credible rooms, real outcomes — platforms aligned with where the industry and policy are actually headed.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-10">
            <SiteImage name="about-teflas-legacy" ratio="3/1" alt="A packed auditorium at a Tefla's event" fallback="https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1500" className="border-white/10" />
          </Reveal>
        </div>
      </section>

      <CtaStrip title="Want the full picture? Get the brochure." />
    </>
  );
}
